// ============================================================================
// RemunerationConfigPanel — bloc CONFIGS GLOBALES de l'onglet Remuneration (AD-D2)
// ============================================================================
// Porte du PoC components/admin/remuneration-section.tsx (sections « Montees
// d'echelon a confirmer », « Couts entreprise par annee », « Cibles de masse
// salariale par annee », « Parametres d'evaluation ») avec les TOKENS DU PARC
// (regle #28, zero classe shadcn). Etat React + formulaires controles
// (P-ADM-14 — jamais de manipulation DOM imperative).
//
// Ces configs sont ADMIN-WIDE (routes /api/admin/remuneration/*, garde
// canAccessGestAdmin serveur) — distinctes du bloc PAR-CIBLE d'AD-D en dessous
// (selection d'un membre -> /api/management/:email/*).
//
// Pourcentages : l'UI saisit des POINTS (0..100, parite PoC), l'API recoit des
// FRACTIONS (0..1, parite stockage). toPoints arrondit a 1 decimale (PoC :
// evite le bruit flottant 15.000...02) ; toFraction arrondit a 6 decimales
// (DECIMAL(8,6) en base).

import { useMemo, useState, type FormEvent } from 'react';
import { BadgeCheck, TrendingUp } from 'lucide-react';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  QueryError,
  SkeletonCard,
  GS_COMPONENTS,
  type Column,
} from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminMember, AdminMemberTeam } from '../../hooks/useAdminMembers';
import {
  useAdminRemunerationConfig,
  useUpsertEvalConfig,
  useUpsertYearConfig,
  useUpsertPayrollTargets,
  useConfirmEchelon,
  remunerationConfigErrorCode,
  type AdminPendingEchelon,
  type AdminYearConfig,
} from '../../hooks/useAdminRemunerationConfig';
import { SectionHeader } from './SectionHeader';

const CAD2 = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' });

/** Fraction -> points, arrondi a 1 decimale (parite PoC toPoints). */
const toPoints = (fraction: number): number => Math.round(fraction * 1000) / 10;
/** Points -> fraction, arrondi a 6 decimales (DECIMAL(8,6)). */
const toFraction = (points: number): number => Math.round(points * 10_000) / 1_000_000;

const inputCls = `${GS_COMPONENTS.input} w-28`;

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: string }) {
  return (
    <label htmlFor={htmlFor} className="text-meta font-medium text-slate-600 dark:text-slate-300">
      {children}
    </label>
  );
}

export function RemunerationConfigPanel({
  members,
  teams,
}: {
  members: AdminMember[];
  teams: AdminMemberTeam[];
}) {
  const { t } = useTranslation();
  const q = useAdminRemunerationConfig(true);
  const [banner, setBanner] = useState<string | null>(null);
  const onError = (err: unknown) => setBanner(remunerationConfigErrorCode(err));

  if (q.isLoading) {
    return <SkeletonCard />;
  }
  if (q.isError || !q.data) {
    return (
      <QueryError
        message={t('gest:admin.remuneration.config.loadError')}
        onRetry={() => void q.refetch()}
      />
    );
  }

  const d = q.data;

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-6 p-5`}>
      <SectionHeader
        title={t('gest:admin.remuneration.config.title')}
        description={t('gest:admin.remuneration.config.description')}
      />

      {banner ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <span>{t(`gest:admin.remuneration.config.errors.${banner}`)}</span>
          <button type="button" onClick={() => setBanner(null)} className="text-chip font-medium underline">
            {t('gest:management.common.dismiss')}
          </button>
        </div>
      ) : null}

      <PendingEchelonsSection
        pending={d.pending}
        maxScore={d.evalConfig?.maxScore ?? null}
        members={members}
        onError={onError}
        onDone={() => setBanner(null)}
      />
      <YearCostSection yearConfigs={d.yearConfigs} onError={onError} onDone={() => setBanner(null)} />
      <PayrollTargetsSection
        yearConfigs={d.yearConfigs}
        teamTargets={d.teamTargets}
        teams={teams}
        onError={onError}
        onDone={() => setBanner(null)}
      />
      <EvalConfigSection
        evalConfig={d.evalConfig}
        onError={onError}
        onDone={() => setBanner(null)}
      />
    </section>
  );
}

// ─── File des montees d'echelon a confirmer (P-D2-4) ─────────────────────────

function PendingEchelonsSection({
  pending,
  maxScore,
  members,
  onError,
  onDone,
}: {
  pending: AdminPendingEchelon[];
  maxScore: number | null;
  members: AdminMember[];
  onError: (err: unknown) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmEchelon();
  const [confirmFor, setConfirmFor] = useState<AdminPendingEchelon | null>(null);

  const nameFor = (email: string): string =>
    members.find((m) => m.email === email)?.fullName?.trim() || email;

  // Defense d'affichage PoC : une proposition d'un employe passe HORS GRILLE ne
  // se confirme pas (le serveur refuse aussi — echelon_off_grid).
  const confirmable = pending.filter((p) => !p.offGrid);
  const skipped = pending.length - confirmable.length;

  function doConfirm() {
    if (!confirmFor) return;
    onDone();
    confirm.mutate(
      { email: confirmFor.email, year: confirmFor.year },
      {
        onSuccess: () => setConfirmFor(null),
        onError: (err: unknown) => {
          setConfirmFor(null);
          onError(err);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.pending.title')}
        description={t('gest:admin.remuneration.config.pending.description')}
      />
      {confirmable.length === 0 ? (
        <EmptyState
          icon={<TrendingUp size={40} aria-hidden="true" />}
          title={t('gest:admin.remuneration.config.pending.emptyTitle')}
          description={
            skipped > 0
              ? t('gest:admin.remuneration.config.pending.skippedOffGrid', { n: skipped })
              : t('gest:admin.remuneration.config.pending.emptyDescription')
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {confirmable.map((p) => (
            <li
              key={p.evaluationId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <div className="min-w-0">
                <p className="text-field font-medium text-slate-800 dark:text-slate-100">
                  {nameFor(p.email)}
                </p>
                <p className="text-chip text-slate-500 dark:text-slate-400">
                  {t('gest:admin.remuneration.config.pending.line', {
                    year: p.year,
                    score: maxScore != null ? `${p.score}/${maxScore}` : String(p.score),
                    date: p.submittedAt.slice(0, 10),
                  })}
                </p>
              </div>
              <button
                type="button"
                className={GS_COMPONENTS.btnAction}
                onClick={() => setConfirmFor(p)}
              >
                <BadgeCheck size={16} aria-hidden="true" className="mr-1.5 inline" />
                {t('gest:admin.remuneration.config.pending.confirm')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {skipped > 0 && confirmable.length > 0 ? (
        <p className="text-chip text-amber-700 dark:text-amber-300">
          {t('gest:admin.remuneration.config.pending.skippedOffGrid', { n: skipped })}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirmFor != null}
        variant="warning"
        title={t('gest:admin.remuneration.config.pending.dialogTitle')}
        message={t('gest:admin.remuneration.config.pending.dialogMessage', {
          name: confirmFor ? nameFor(confirmFor.email) : '',
          year: confirmFor?.year ?? '',
        })}
        confirmLabel={t('gest:admin.remuneration.config.pending.confirm')}
        cancelLabel={t('gest:management.common.cancel')}
        isLoading={confirm.isPending}
        onConfirm={doConfirm}
        onCancel={() => setConfirmFor(null)}
      />
    </div>
  );
}

// ─── Couts entreprise par annee (#13, vehicule / 2 semaines) ─────────────────

function YearCostSection({
  yearConfigs,
  onError,
  onDone,
}: {
  yearConfigs: AdminYearConfig[];
  onError: (err: unknown) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertYearConfig();
  const currentYear = new Date().getFullYear();
  const costFor = (year: number): string => {
    const c = yearConfigs.find((y) => y.year === year)?.vehicleCostBiweekly;
    return c == null ? '' : String(c);
  };
  const [year, setYear] = useState(String(currentYear));
  const [cost, setCost] = useState(() => costFor(currentYear));

  const rows = yearConfigs.filter((y) => y.vehicleCostBiweekly != null);
  const columns: Column<AdminYearConfig>[] = [
    { key: 'year', header: t('gest:admin.remuneration.config.costs.year'), render: (r) => String(r.year) },
    {
      key: 'cost',
      header: t('gest:admin.remuneration.config.costs.biweekly'),
      render: (r) => (r.vehicleCostBiweekly == null ? '—' : CAD2.format(r.vehicleCostBiweekly)),
    },
    {
      key: 'annual',
      header: t('gest:admin.remuneration.config.costs.annual'),
      render: (r) => (r.vehicleCostBiweekly == null ? '—' : CAD2.format(r.vehicleCostBiweekly * 26)),
    },
  ];

  function submit(e: FormEvent) {
    e.preventDefault();
    const y = Number(year);
    const c = Number(cost);
    if (!Number.isInteger(y) || y < 2000 || y > 2100 || !Number.isFinite(c) || c < 0) return;
    onDone();
    upsert.mutate({ year: y, vehicleCostBiweekly: c }, { onError });
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.costs.title')}
        description={t('gest:admin.remuneration.config.costs.description')}
      />
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="ad2-cost-year">{t('gest:admin.remuneration.config.costs.year')}</FieldLabel>
          <input
            id="ad2-cost-year"
            type="number"
            min={2000}
            max={2100}
            step={1}
            required
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              const y = Number(e.target.value);
              if (Number.isInteger(y)) setCost(costFor(y));
            }}
            className={inputCls}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="ad2-cost-vehicle">{t('gest:admin.remuneration.config.costs.biweekly')}</FieldLabel>
          <input
            id="ad2-cost-vehicle"
            type="number"
            min={0}
            max={100000}
            step="0.01"
            required
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className={`${GS_COMPONENTS.input} w-44`}
          />
        </div>
        <button type="submit" disabled={upsert.isPending} className={GS_COMPONENTS.btnAction}>
          {t('gest:admin.remuneration.config.costs.save')}
        </button>
      </form>
      {rows.length === 0 ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:admin.remuneration.config.costs.empty')}
        </p>
      ) : (
        <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
          <DataTable columns={columns} data={rows} rowKey={(r) => String(r.year)} />
        </div>
      )}
    </div>
  );
}

// ─── Cibles de masse salariale par annee (#14) ───────────────────────────────

interface TargetsTableRow {
  year: number;
  revenuePct: number | null;
  teamCount: number;
  sum: number;
}

function PayrollTargetsSection({
  yearConfigs,
  teamTargets,
  teams,
  onError,
  onDone,
}: {
  yearConfigs: AdminYearConfig[];
  teamTargets: { year: number; teamId: string; targetPct: number }[];
  teams: AdminMemberTeam[];
  onError: (err: unknown) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertPayrollTargets();
  const currentYear = new Date().getFullYear();

  const targetsFor = (year: number): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const t2 of teamTargets) {
      if (t2.year === year) map[t2.teamId] = String(toPoints(t2.targetPct));
    }
    return map;
  };
  const revenueFor = (year: number): string => {
    const r = yearConfigs.find((y) => y.year === year)?.revenuePct;
    // Defaut PoC : 15 points quand aucune cible n'existe pour l'annee.
    return r == null ? '15' : String(toPoints(r));
  };

  const [year, setYear] = useState(String(currentYear));
  const [revenue, setRevenue] = useState(() => revenueFor(currentYear));
  const [teamPts, setTeamPts] = useState<Record<string, string>>(() => targetsFor(currentYear));

  const sumPts = useMemo(
    () => Object.values(teamPts).reduce((s, v) => s + (v.trim() === '' ? 0 : Number(v) || 0), 0),
    [teamPts],
  );

  const tableRows: TargetsTableRow[] = useMemo(() => {
    const years = new Set<number>();
    for (const y of yearConfigs) if (y.revenuePct != null) years.add(y.year);
    for (const t2 of teamTargets) years.add(t2.year);
    return [...years]
      .sort((a, b) => b - a)
      .map((y) => {
        const targets = teamTargets.filter((t2) => t2.year === y);
        return {
          year: y,
          revenuePct: yearConfigs.find((c) => c.year === y)?.revenuePct ?? null,
          teamCount: targets.length,
          sum: targets.reduce((s, t2) => s + t2.targetPct, 0),
        };
      });
  }, [yearConfigs, teamTargets]);

  const columns: Column<TargetsTableRow>[] = [
    { key: 'year', header: t('gest:admin.remuneration.config.targets.year'), render: (r) => String(r.year) },
    {
      key: 'revenue',
      header: t('gest:admin.remuneration.config.targets.revenueShort'),
      render: (r) => (r.revenuePct == null ? '—' : `${toPoints(r.revenuePct)} %`),
    },
    {
      key: 'teams',
      header: t('gest:admin.remuneration.config.targets.teamsTargeted'),
      render: (r) => (r.teamCount === 0 ? '—' : String(r.teamCount)),
    },
    {
      key: 'sum',
      header: t('gest:admin.remuneration.config.targets.teamsSum'),
      render: (r) => (r.teamCount === 0 ? '—' : `${toPoints(r.sum)} %`),
    },
  ];

  function loadYear(value: string) {
    setYear(value);
    const y = Number(value);
    if (Number.isInteger(y)) {
      setRevenue(revenueFor(y));
      setTeamPts(targetsFor(y));
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const y = Number(year);
    const rev = Number(revenue);
    if (!Number.isInteger(y) || y < 2000 || y > 2100 || !Number.isFinite(rev) || rev < 0 || rev > 100) return;
    const targets: { teamId: string; targetPct: number }[] = [];
    for (const [teamId, raw] of Object.entries(teamPts)) {
      if (raw.trim() === '') continue; // vide = pas de cible (parite PoC)
      const pts = Number(raw);
      if (!Number.isFinite(pts) || pts < 0 || pts > 100) return;
      targets.push({ teamId, targetPct: toFraction(pts) });
    }
    onDone();
    upsert.mutate({ year: y, revenuePct: toFraction(rev), targets }, { onError });
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.targets.title')}
        description={t('gest:admin.remuneration.config.targets.description')}
      />
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-targets-year">{t('gest:admin.remuneration.config.targets.year')}</FieldLabel>
            <input
              id="ad2-targets-year"
              type="number"
              min={2000}
              max={2100}
              step={1}
              required
              value={year}
              onChange={(e) => loadYear(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-targets-revenue">{t('gest:admin.remuneration.config.targets.revenue')}</FieldLabel>
            <input
              id="ad2-targets-revenue"
              type="number"
              min={0}
              max={100}
              step="0.1"
              required
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              className={`${GS_COMPONENTS.input} w-44`}
            />
          </div>
        </div>

        {teams.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:admin.remuneration.config.targets.perTeam')}
            </span>
            <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center justify-between gap-3">
                  <label
                    htmlFor={`ad2-target-${team.id}`}
                    className="truncate text-meta text-slate-500 dark:text-slate-400"
                  >
                    {team.name}
                  </label>
                  <input
                    id={`ad2-target-${team.id}`}
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={teamPts[team.id] ?? ''}
                    onChange={(e) => setTeamPts((prev) => ({ ...prev, [team.id]: e.target.value }))}
                    className={`${GS_COMPONENTS.input} w-24`}
                  />
                </div>
              ))}
            </div>
            <p
              className={`text-chip ${sumPts > 100 ? 'font-medium text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}
            >
              {t('gest:admin.remuneration.config.targets.sumLine', { n: Math.round(sumPts * 10) / 10 })}
            </p>
          </div>
        ) : null}

        <div>
          <button type="submit" disabled={upsert.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:admin.remuneration.config.targets.save')}
          </button>
        </div>
      </form>

      {tableRows.length === 0 ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:admin.remuneration.config.targets.empty')}
        </p>
      ) : (
        <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
          <DataTable columns={columns} data={tableRows} rowKey={(r) => String(r.year)} />
        </div>
      )}
    </div>
  );
}

// ─── Parametres d'evaluation (#12) ───────────────────────────────────────────

function EvalConfigSection({
  evalConfig,
  onError,
  onDone,
}: {
  evalConfig: { passThreshold: number; maxScore: number; criteria: { label: string }[] } | null;
  onError: (err: unknown) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertEvalConfig();
  // Defauts PoC : seuil 3, max 5 quand aucune config n'existe.
  const [threshold, setThreshold] = useState(() => String(evalConfig?.passThreshold ?? 3));
  const [maxScore, setMaxScore] = useState(() => String(evalConfig?.maxScore ?? 5));
  const [criteria, setCriteria] = useState(() =>
    (evalConfig?.criteria ?? []).map((c) => c.label).join('\n'),
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    const pass = Number(threshold);
    const max = Number(maxScore);
    if (!Number.isFinite(pass) || pass < 0 || !Number.isFinite(max) || max < 1) return;
    onDone();
    // Le textarea entier est TOUJOURS renvoye (parite PoC) — criteria n'est
    // jamais omis par CE formulaire ; l'omission (P-D2-2) sert les clients
    // partiels de l'API.
    upsert.mutate({ passThreshold: pass, maxScore: max, criteria }, { onError });
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.eval.title')}
        description={t('gest:admin.remuneration.config.eval.description')}
      />
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-eval-threshold">{t('gest:admin.remuneration.config.eval.threshold')}</FieldLabel>
            <input
              id="ad2-eval-threshold"
              type="number"
              min={0}
              max={1000}
              step="0.5"
              required
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-eval-max">{t('gest:admin.remuneration.config.eval.maxScore')}</FieldLabel>
            <input
              id="ad2-eval-max"
              type="number"
              min={1}
              max={1000}
              step="0.5"
              required
              value={maxScore}
              onChange={(e) => setMaxScore(e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="ad2-eval-criteria">{t('gest:admin.remuneration.config.eval.criteria')}</FieldLabel>
          <textarea
            id="ad2-eval-criteria"
            rows={4}
            maxLength={2000}
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            placeholder={t('gest:admin.remuneration.config.eval.criteriaPlaceholder')}
            className={`${GS_COMPONENTS.input} h-auto`}
          />
          <p className="text-chip text-slate-500 dark:text-slate-400">
            {t('gest:admin.remuneration.config.eval.criteriaHint')}
          </p>
        </div>
        <div>
          <button type="submit" disabled={upsert.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:admin.remuneration.config.eval.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
